import { ENDPOINTS } from '../config/api';
import { apiJson } from './httpClient';

export const getModels = async () => {
  try {
    const data = await apiJson(ENDPOINTS.MODELS, {
      method: "GET",
    });

    return data;
  } catch (error) {
    console.error('Error fetching models:', error);
    throw error;
  }
};

export const generateSummary = async (text, options = {}) => {
  const {
    model = "gpt-5-mini",
    temperature = 0.0,
    top_p = 0.05,
    method = "Default",
    intendedAudience = "general",
    summaryStyle = "concise"
  } = options;
  
  try {
    const data = await apiJson(ENDPOINTS.SUMMARIZE, {
      method: "POST", 
      body: JSON.stringify({
        text,
        model,
        temperature,
        top_p,
        method,
        intended_audience: intendedAudience,
        summary_style: summaryStyle,
      }),
    });

    return data.summary;
  } catch (error) {
    throw error;
  }
};

export const postProcessText = async (operation, text, options = {}) => {
  const {
    model = "gpt-5-mini",
    temperature = 0.0,
    top_p = 0.05
  } = options;
  
  const operationsMap = {
    "Shorten": ENDPOINTS.SHORTEN,
    "Simplify": ENDPOINTS.SIMPLIFY, 
    "Rephrase": ENDPOINTS.REPHRASE,
  };

  if (!(operation in operationsMap)) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(text + ` [${operation} applied]`), 500);
    });
  }

  try {
    const data = await apiJson(operationsMap[operation], {
      method: "POST",
      body: JSON.stringify({
        text,
        model,
        temperature,
        top_p,
      }),
    });

    return data.summary;
  } catch (error) {
    throw error;
  }
};

/**
 * Enhance a custom use case description into a full prompt template.
 * @param {string} useCaseDescription - The user's custom use case description
 * @param {object} options - Additional options (audience, style, model)
 * @returns {Promise<{success: boolean, enhanced_prompt: string}>}
 */
export const enhanceUseCase = async (useCaseDescription, options = {}) => {
  const {
    audience = "general",
    style = "concise",
    model = null,
  } = options;

  try {
    return await apiJson(ENDPOINTS.ENHANCE_USE_CASE, {
      method: "POST",
      body: JSON.stringify({
        use_case_description: useCaseDescription,
        audience,
        style,
        model,
      }),
    });
  } catch (error) {
    console.error('Error enhancing use case:', error);
    throw error;
  }
};

export const editSelection = async (text, instruction, options = {}) => {
  const {
    model = "gpt-5-mini",
    temperature = 0.3,
    top_p = 0.05
  } = options;
  
  try {
    const data = await apiJson(ENDPOINTS.EDIT_SELECTION, {
      method: "POST",
      body: JSON.stringify({
        text,
        instruction,
        model,
        temperature,
        top_p,
      }),
    });

    return data.edited_text;
  } catch (error) {
    throw error;
  }
};

export const extractMetadata = async (text, summary, options = {}) => {
  const {
    model = "gpt-5-mini",
    temperature = 0.0,
    top_p = 0.05
  } = options;
  
  try {
    return await apiJson(ENDPOINTS.EXTRACT_METADATA, {
      method: "POST",
      body: JSON.stringify({
        text,
        summary,
        model,
        temperature,
        top_p,
      }),
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Get AI-powered edit suggestions for a generated summary.
 * @param {string} text - The generated summary text.
 * @param {object} options - Additional options (source_text, model, temperature).
 * @returns {Promise<{success: boolean, suggestions: Array}>}
 */
export const getSuggestions = async (text, options = {}) => {
  const {
    source_text = null,
     intended_audience = null,
    model = null,
    temperature = null,
  } = options;

  return apiJson(ENDPOINTS.SUGGESTIONS, {
    method: "POST",
    body: JSON.stringify({
      text,
      source_text,
      intended_audience,
      model,
      temperature,
    }),
  });
};

/**
 * Apply a single edit suggestion to the summary.
 * @param {string} text - The current summary text.
 * @param {object} suggestion - The suggestion to apply (title, description).
 * @param {object} options - Additional options (source_text, model, temperature).
 * @returns {Promise<{success: boolean, edited_text: string}>}
 */
export const applySuggestion = async (text, suggestion, options = {}) => {
  const {
    source_text = null,
    model = null,
    temperature = null,
  } = options;

  return apiJson(ENDPOINTS.SUGGESTIONS_APPLY, {
    method: "POST",
    body: JSON.stringify({
      text,
      source_text,
      suggestion_title: suggestion.title,
      suggestion_description: suggestion.description,
      model,
      temperature,
    }),
  });
};
